import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, Landmark, Calendar, Building2, 
  CheckCircle, Award, Users, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const highlights = [
  { icon: Landmark, value: '50+', label: 'Government Clients' },
  { icon: Award, value: '100+', label: 'Projects Delivered' },
  { icon: Users, value: '10M+', label: 'Citizens Served' },
  { icon: Clock, value: '15+', label: 'Years Experience' }
];

const departments = [
  'Ministry of Electronics & IT',
  'Ministry of Health',
  'Ministry of Transport',
  'State Revenue Departments',
  'Smart City Missions',
  'Public Sector Banks',
  'State Road Transport',
  'Municipal Corporations'
];

export default function GovernmentProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects?category=government`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  return (
    <div data-testid="government-projects-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-nstu-green-800" data-testid="gov-projects-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Landmark className="w-10 h-10 text-nstu-gold-400" />
              <Badge className="bg-nstu-gold-400 text-nstu-green-900 text-lg px-4 py-1">Featured</Badge>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Government IT Projects
            </h1>
            <p className="text-gray-300 text-lg mb-8">
              Driving digital transformation across ministries, state governments, and public sector 
              organizations. Our government projects have touched the lives of millions of citizens 
              across India.
            </p>
            <Link to="/contact">
              <Button className="bg-nstu-gold-400 text-nstu-green-900 hover:bg-nstu-gold-300 font-semibold btn-lift" data-testid="gov-hero-cta">
                Discuss Your Project
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-white border-b" data-testid="gov-highlights">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {highlights.map((item) => {
              const IconComponent = item.icon;
              return (
                <div key={item.label} className="text-center">
                  <div className="w-16 h-16 rounded-full bg-nstu-green-100 flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-8 h-8 text-nstu-green-800" />
                  </div>
                  <div className="font-heading text-3xl font-bold text-nstu-green-800 mb-1">
                    {item.value}
                  </div>
                  <p className="text-gray-600">{item.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-gray-50" data-testid="gov-projects-grid">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-nstu-green-100 text-nstu-green-800 mb-4">Our Work</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-nstu-green-800 mb-4">
              Government Project Portfolio
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Showcasing our successful implementations across various government departments and initiatives
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse overflow-hidden">
                  <div className="aspect-video bg-gray-200" />
                  <CardContent className="p-6">
                    <div className="h-6 bg-gray-200 rounded mb-3 w-3/4" />
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-5/6" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {projects.map((project) => (
                <Card 
                  key={project.id}
                  className="group overflow-hidden border-0 shadow-lg card-hover"
                  data-testid={`gov-project-card-${project.id}`}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img 
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-nstu-gold-400 text-nstu-green-900 font-semibold">
                        <Landmark className="w-3 h-3 mr-1" />
                        Government
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <Calendar className="w-4 h-4" />
                      <span>{project.year}</span>
                      <span className="mx-2">•</span>
                      <Building2 className="w-4 h-4" />
                      <span>{project.client}</span>
                    </div>
                    <h3 className="font-heading text-xl font-bold text-nstu-green-800 mb-3">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech) => (
                        <span 
                          key={tech}
                          className="px-3 py-1 bg-nstu-green-50 text-nstu-green-700 rounded-full text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Departments We Serve */}
      <section className="py-20 bg-white" data-testid="departments">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-nstu-gold-100 text-nstu-gold-700 mb-4">Our Reach</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-nstu-green-800 mb-6">
                Government Departments We Serve
              </h2>
              <p className="text-gray-600 mb-8">
                We have successfully partnered with numerous government departments and public sector 
                organizations across India, delivering mission-critical IT solutions that enhance 
                citizen services and administrative efficiency.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {departments.map((dept) => (
                  <div 
                    key={dept}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-nstu-green-50 transition-colors"
                  >
                    <CheckCircle className="w-5 h-5 text-nstu-gold-500 flex-shrink-0" />
                    <span className="text-gray-700 text-sm font-medium">{dept}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1760872645959-98d5fdb49287?w=800"
                alt="Indian Parliament Building"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-nstu-green-800 text-white p-6 rounded-xl shadow-xl">
                <Landmark className="w-8 h-8 text-nstu-gold-400 mb-2" />
                <p className="font-bold text-2xl">Pan-India</p>
                <p className="text-sm text-gray-300">Coverage</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Government Clients Choose Us */}
      <section className="py-20 bg-nstu-green-800" data-testid="why-choose-gov">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-nstu-gold-400 text-nstu-green-900 mb-4">Our Expertise</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Why Government Clients Choose NSTU
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Security Compliance',
                description: 'ISO 27001 certified processes ensuring data security and privacy compliance'
              },
              {
                title: 'Government Empanelment',
                description: 'STQC empanelled and GeM registered vendor with proven track record'
              },
              {
                title: 'Domain Expertise',
                description: '15+ years of specialized experience in e-Governance projects'
              },
              {
                title: 'Scalable Solutions',
                description: 'Solutions designed to handle millions of transactions and users'
              },
              {
                title: 'Dedicated Support',
                description: '24/7 support teams with SLA-driven response and resolution'
              },
              {
                title: 'Pan-India Presence',
                description: 'Local teams across India for seamless project delivery and support'
              }
            ].map((item) => (
              <Card key={item.title} className="bg-white/10 backdrop-blur border-0">
                <CardContent className="p-6">
                  <CheckCircle className="w-8 h-8 text-nstu-gold-400 mb-4" />
                  <h3 className="font-heading text-lg font-bold text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-300 text-sm">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50" data-testid="gov-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-nstu-green-800 mb-4">
            Have a Government Project in Mind?
          </h2>
          <p className="text-gray-600 mb-8">
            Let's discuss how NSTU can help your department achieve its digital transformation goals
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-nstu-green-800 text-white hover:bg-nstu-green-700 btn-lift" data-testid="gov-bottom-cta">
                Start a Conversation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-nstu-green-800 text-nstu-green-800 hover:bg-nstu-green-800 hover:text-white">
                Explore Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
